import { bindPrototypeMethodsToSelf } from '@wix/panorama-common/utils';
import {
  InternalTransactions,
  type PhaseName,
  type TransactionUserData,
} from '@wix/panorama-common-shared/types';
import { type CustomTransactionName } from '@wix/panorama-common-shared/utils';
import {
  type PanoramaClientForComponent,
  PanoramaClientForComponentImpl,
} from './panorama-client-component';
import type { BasePanoramaClient } from './panorama-client-base';
import {
  Hook,
  type HooksManager,
  type SubscriberByHook,
} from './plugins/hooks-manager';
import {
  type PanoramaErrorMonitor,
  PanoramaErrorMonitorImpl,
} from './panorama-error-monitor';
import { PanoramaLogger, PanoramaLoggerImpl } from './panorama-logger';
import type {
  ErrorMonitorOptions,
  ErrorMonitorUserData,
  TransactionOptions,
} from './types';
import {
  type PanoramaTransaction,
  PanoramaTransactionImpl,
} from './panorama-transaction';
import { type PanoramaPhase, PanoramaPhaseImpl } from './panorama-phase';

interface Options {
  hooksManager: HooksManager;
  baseClient: BasePanoramaClient;
}

export interface PanoramaClient {
  onUnhandledError(
    subscriber: SubscriberByHook[Hook.beforeUnhandledError],
  ): void;

  reportLoadStart(
    data?: TransactionUserData,
    options?: TransactionOptions,
  ): void;

  reportLoadFinish(
    data?: TransactionUserData,
    options?: TransactionOptions,
  ): void;

  phase(name: PhaseName): PanoramaPhase;

  transaction<T extends string>(
    name: CustomTransactionName<T>,
    options?: TransactionOptions,
  ): PanoramaTransaction;

  errorMonitor(
    options?: ErrorMonitorOptions,
  ): PanoramaErrorMonitor<ErrorMonitorUserData>;

  logger(): PanoramaLogger;

  createClientForComponent(): PanoramaClientForComponent;
}

export class PanoramaClientImpl implements PanoramaClient {
  private baseClient: BasePanoramaClient;
  private hooksManager: HooksManager;

  constructor({ hooksManager, baseClient }: Options) {
    this.baseClient = baseClient;
    this.hooksManager = hooksManager;

    bindPrototypeMethodsToSelf(this);
  }

  onUnhandledError(subscriber: SubscriberByHook[Hook.beforeUnhandledError]) {
    this.hooksManager.tap(Hook.beforeUnhandledError, subscriber);
  }

  reportLoadStart(data?: TransactionUserData, options?: TransactionOptions) {
    const { ComponentLoad } = InternalTransactions;

    return this.baseClient.reportTransactionStart(ComponentLoad, options, data);
  }

  reportLoadFinish(data?: TransactionUserData, options?: TransactionOptions) {
    const { ComponentLoad } = InternalTransactions;

    return this.baseClient.reportTransactionFinish(
      ComponentLoad,
      options,
      data,
    );
  }

  phase(name: PhaseName) {
    return new PanoramaPhaseImpl(name, this.baseClient);
  }

  transaction(
    name: CustomTransactionName<string>,
    options?: TransactionOptions,
  ) {
    return new PanoramaTransactionImpl(this.baseClient, name, options);
  }

  errorMonitor(
    options?: ErrorMonitorOptions,
  ): PanoramaErrorMonitor<ErrorMonitorUserData> {
    return new PanoramaErrorMonitorImpl<ErrorMonitorUserData>(
      this.baseClient,
      options,
    );
  }

  logger(): PanoramaLogger {
    return new PanoramaLoggerImpl(this.baseClient);
  }

  createClientForComponent(): PanoramaClientForComponent {
    const client = new PanoramaClientForComponentImpl({
      baseClient: this.baseClient,
    });

    this.hooksManager.invoke(Hook.afterCreateClientForComponent, client);

    return client;
  }
}
